import { PatientDocument } from 'common/enum/patient-document'
import { FileValidator } from 'common/utils/file-validator'
import { RetryHandler } from 'common/utils/retry-handler'
import { getUploadLogger, UploadLogger } from 'common/utils/upload-logger'
import { UploadStage } from 'common/utils/upload-error-messages'
import { S3UploadService, S3UploadError } from './s3-upload-service'
import {
  UploadDocumentRequest,
  UploadDocumentResponse,
  ProcessDocumentRequest,
  ProcessDocumentResponse,
  UploadResult,
  UserContext
} from 'domain/entities/upload-document'

const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.')
  if (parts.length <= 1) return ''
  return `.${(parts.pop() ?? '').toLowerCase()}`
}

export type UploadServiceDependencies = {
  fileValidator?: FileValidator
  retryHandler?: RetryHandler
  s3UploadService?: S3UploadService
  logger?: UploadLogger
}

export class UploadService {
  private readonly fileValidator: FileValidator
  private readonly retryHandler: RetryHandler
  private readonly s3UploadService: S3UploadService
  private readonly logger: UploadLogger
  private readonly requestPresignedUrl: (
    params: UploadDocumentRequest
  ) => Promise<UploadDocumentResponse>
  private readonly notifyDocumentProcessing: (
    params: ProcessDocumentRequest
  ) => Promise<ProcessDocumentResponse>

  constructor(
    requestPresignedUrl: (
      params: UploadDocumentRequest
    ) => Promise<UploadDocumentResponse>,
    notifyDocumentProcessing: (
      params: ProcessDocumentRequest
    ) => Promise<ProcessDocumentResponse>,
    dependencies: UploadServiceDependencies = {}
  ) {
    this.fileValidator = dependencies.fileValidator ?? new FileValidator()
    this.retryHandler = dependencies.retryHandler ?? new RetryHandler()
    this.s3UploadService = dependencies.s3UploadService ?? new S3UploadService()
    this.logger = dependencies.logger ?? getUploadLogger()
    this.requestPresignedUrl = requestPresignedUrl
    this.notifyDocumentProcessing = notifyDocumentProcessing
  }

  async uploadDocument(
    file: File,
    surgicalOrderId: number,
    documentType: PatientDocument,
    user: UserContext
  ): Promise<string> {
    const context = {
      fileName: file.name,
      fileSize: file.size,
      surgicalOrderId,
      userId: user.id
    }

    try {
      this.fileValidator.validateFileType(file)
      this.fileValidator.validateFileSize(file)
    } catch (error) {
      this.logger.logValidationError(
        file.name,
        error instanceof Error ? error.message : String(error),
        context
      )
      throw error
    }

    let presignedData: UploadDocumentResponse

    try {
      const stageStartTime = Date.now()
      this.logger.logStageStart(UploadStage.REQUEST_URL, context)

      presignedData = await this.retryHandler.executeWithRetry(async () => {
        this.logger.logPresignedUrlRequest(file.name, file.type, context)
        return await this.requestPresignedUrl({
          document_name: file.name,
          document_type: file.type,
          document_extension: getFileExtension(file.name),
          surgical_order_id: surgicalOrderId
        })
      })

      this.logger.logPresignedUrlReceived(presignedData.document_id, {
        ...context,
        documentId: presignedData.document_id,
        key: presignedData.key
      })
      this.logger.logStageSuccess(
        UploadStage.REQUEST_URL,
        Date.now() - stageStartTime,
        context
      )
    } catch (error) {
      this.logger.logStageFailure(UploadStage.REQUEST_URL, error, context)
      this.logger.logUploadFailure(file.name, error, context)
      throw new Error(
        `Failed to request presigned URL: ${
          error instanceof Error ? error.message : String(error)
        }`
      )
    }

    try {
      const stageStartTime = Date.now()
      this.logger.logStageStart(UploadStage.S3_UPLOAD, {
        ...context,
        documentId: presignedData.document_id
      })

      await this.retryHandler.executeWithRetry(async () => {
        this.logger.logS3UploadStart(file.name, file.size, {
          ...context,
          documentId: presignedData.document_id
        })

        try {
          await this.s3UploadService.uploadToS3(
            presignedData.url,
            file,
            file.type
          )
          this.logger.logS3UploadSuccess(file.name, {
            ...context,
            documentId: presignedData.document_id
          })
        } catch (error) {
          if (error instanceof S3UploadError && error.code === 'URL_EXPIRED') {
            this.logger.warn(
              'URL expired (403), requesting new presigned URL',
              {
                ...context,
                documentId: presignedData.document_id
              }
            )

            presignedData = await this.requestPresignedUrl({
              document_name: file.name,
              document_type: file.type,
              document_extension: getFileExtension(file.name),
              surgical_order_id: surgicalOrderId
            })

            this.logger.logPresignedUrlReceived(presignedData.document_id, {
              ...context,
              documentId: presignedData.document_id,
              reason: 'url_expired_retry'
            })

            await this.s3UploadService.uploadToS3(
              presignedData.url,
              file,
              file.type
            )

            this.logger.logS3UploadSuccess(file.name, {
              ...context,
              documentId: presignedData.document_id,
              retriedWithNewUrl: true
            })
            return
          }

          throw error
        }
      })

      this.logger.logStageSuccess(
        UploadStage.S3_UPLOAD,
        Date.now() - stageStartTime,
        { ...context, documentId: presignedData.document_id }
      )
    } catch (error) {
      this.logger.logStageFailure(UploadStage.S3_UPLOAD, error, {
        ...context,
        documentId: presignedData.document_id
      })
      this.logger.logUploadFailure(file.name, error, context)
      throw new Error(
        `Failed to upload to S3: ${
          error instanceof Error ? error.message : String(error)
        }`
      )
    }

    try {
      const stageStartTime = Date.now()
      this.logger.logStageStart(UploadStage.NOTIFICATION, {
        ...context,
        documentId: presignedData.document_id
      })

      await this.retryHandler.executeWithRetry(async () => {
        this.logger.logNotificationStart(presignedData.document_id, {
          ...context,
          surgicalOrderId
        })

        const s3Url = presignedData.key

        await this.notifyDocumentProcessing({
          document_id: presignedData.document_id,
          document_name: file.name,
          document_type: documentType,
          document_url: s3Url,
          document_extension: getFileExtension(file.name),
          created_by: user.id,
          created_by_name: user.name,
          surgical_order_id: surgicalOrderId,
          event_type: 'SURGICAL_DOCUMENT_UPLOADED'
        })

        this.logger.logNotificationSuccess(presignedData.document_id, {
          ...context,
          surgicalOrderId
        })
      })

      this.logger.logStageSuccess(
        UploadStage.NOTIFICATION,
        Date.now() - stageStartTime,
        { ...context, documentId: presignedData.document_id }
      )
    } catch (error) {
      this.logger.logStageFailure(UploadStage.NOTIFICATION, error, {
        ...context,
        documentId: presignedData.document_id
      })
      this.logger.logUploadFailure(file.name, error, context)
      throw new Error(
        `Failed to notify backend: ${
          error instanceof Error ? error.message : String(error)
        }`
      )
    }

    return presignedData.document_id
  }

  async uploadMultipleDocuments(
    files: File[],
    surgicalOrderId: number,
    documentType: PatientDocument,
    user: UserContext,
    onProgress?: (completed: number, total: number) => void
  ): Promise<UploadResult[]> {
    const context = {
      fileCount: files.length,
      surgicalOrderId,
      userId: user.id,
      documentType
    }

    this.logger.info('Starting multiple file upload', context)

    const results: UploadResult[] = []
    let completed = 0

    const presignedDataList: Array<{
      file: File
      presignedData?: UploadDocumentResponse
      error?: Error
    }> = []

    for (const file of files) {
      try {
        this.fileValidator.validateFileType(file)
        this.fileValidator.validateFileSize(file)

        const presignedData = await this.retryHandler.executeWithRetry(
          async () => {
            this.logger.logPresignedUrlRequest(file.name, file.type, {
              ...context,
              fileName: file.name
            })
            return await this.requestPresignedUrl({
              document_name: file.name,
              document_type: file.type,
              document_extension: getFileExtension(file.name),
              surgical_order_id: surgicalOrderId
            })
          }
        )

        presignedDataList.push({ file, presignedData })
        this.logger.logPresignedUrlReceived(presignedData.document_id, {
          ...context,
          fileName: file.name,
          documentId: presignedData.document_id
        })
      } catch (error) {
        this.logger.error('Failed to get presigned URL for file', error, {
          ...context,
          fileName: file.name
        })
        presignedDataList.push({
          file,
          error: error instanceof Error ? error : new Error(String(error))
        })
      }
    }

    const uploadPromises = presignedDataList.map(async (item) => {
      if (item.error || !item.presignedData) {
        return {
          file: item.file,
          success: false,
          error: item.error || new Error('No presigned URL available')
        }
      }

      try {
        await this.retryHandler.executeWithRetry(async () => {
          this.logger.logS3UploadStart(item.file.name, item.file.size, {
            ...context,
            fileName: item.file.name,
            documentId: item.presignedData!.document_id
          })

          try {
            await this.s3UploadService.uploadToS3(
              item.presignedData!.url,
              item.file,
              item.file.type
            )
          } catch (error) {
            if (
              error instanceof S3UploadError &&
              error.code === 'URL_EXPIRED'
            ) {
              this.logger.warn('URL expired for file, requesting new URL', {
                ...context,
                fileName: item.file.name,
                documentId: item.presignedData!.document_id
              })

              const newPresignedData = await this.requestPresignedUrl({
                document_name: item.file.name,
                document_type: item.file.type,
                document_extension: getFileExtension(item.file.name),
                surgical_order_id: surgicalOrderId
              })
              item.presignedData = newPresignedData

              this.logger.logPresignedUrlReceived(
                newPresignedData.document_id,
                {
                  ...context,
                  fileName: item.file.name,
                  documentId: newPresignedData.document_id,
                  reason: 'url_expired_retry'
                }
              )

              await this.s3UploadService.uploadToS3(
                newPresignedData.url,
                item.file,
                item.file.type
              )
              return
            }
            throw error
          }
        })

        this.logger.logS3UploadSuccess(item.file.name, {
          ...context,
          fileName: item.file.name,
          documentId: item.presignedData!.document_id
        })

        return {
          file: item.file,
          presignedData: item.presignedData,
          success: true
        }
      } catch (error) {
        this.logger.error('S3 upload failed for file', error, {
          ...context,
          fileName: item.file.name
        })
        return {
          file: item.file,
          success: false,
          error: error instanceof Error ? error : new Error(String(error))
        }
      }
    })

    const uploadResults = await Promise.all(uploadPromises)

    for (const uploadResult of uploadResults) {
      if (!uploadResult.success || !('presignedData' in uploadResult)) {
        results.push({
          file: uploadResult.file,
          success: false,
          error: uploadResult.error
        })
        completed++
        this.logger.logMultiUploadProgress(completed, files.length, context)
        if (onProgress) {
          onProgress(completed, files.length)
        }
        continue
      }

      try {
        await this.retryHandler.executeWithRetry(async () => {
          this.logger.logNotificationStart(
            uploadResult.presignedData!.document_id,
            {
              ...context,
              fileName: uploadResult.file.name,
              documentId: uploadResult.presignedData!.document_id
            }
          )

          const s3URL = uploadResult.presignedData!.key

          await this.notifyDocumentProcessing({
            document_id: uploadResult.presignedData!.document_id,
            document_name: uploadResult.file.name,
            document_type: documentType,
            document_url: s3URL,
            document_extension: getFileExtension(uploadResult.file.name),
            created_by: user.id,
            created_by_name: user.name,
            surgical_order_id: surgicalOrderId,
            event_type: 'SURGICAL_DOCUMENT_UPLOADED'
          })

          this.logger.logNotificationSuccess(
            uploadResult.presignedData!.document_id,
            {
              ...context,
              fileName: uploadResult.file.name,
              documentId: uploadResult.presignedData!.document_id
            }
          )
        })

        results.push({
          file: uploadResult.file,
          document_id: uploadResult.presignedData!.document_id,
          success: true
        })
      } catch (error) {
        this.logger.error('Backend notification failed for file', error, {
          ...context,
          fileName: uploadResult.file.name,
          documentId: uploadResult.presignedData!.document_id
        })
        results.push({
          file: uploadResult.file,
          success: false,
          error: error instanceof Error ? error : new Error(String(error))
        })
      }

      completed++
      this.logger.logMultiUploadProgress(completed, files.length, context)
      if (onProgress) {
        onProgress(completed, files.length)
      }
    }

    const successCount = results.filter((r) => r.success).length
    const failureCount = results.filter((r) => !r.success).length

    this.logger.info('Multiple file upload completed', {
      ...context,
      total: files.length,
      successful: successCount,
      failed: failureCount
    })

    return results
  }
}
